import { useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { MoreOutlined, DownOutlined } from "@ant-design/icons"
import { TicketedIcon } from "ui/Icon/DSIcon/Ticketed"
import { numberToCurrency } from "helpers/string"
import { Text } from "ui/Typography"
import CrewCell from "../../BookingTable/CrewCell"
import ResourceCell from "../../BookingTable/ResourceCell"
import NotesSection from "./NotesSection"
import styles from "./index.module.css"

const toTitleCase = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "")

// Collapsible section card (accordion) — header shows the title + count + chevron.
const CollapsibleSection = ({ title, count, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={styles.sectionCard}>
      <button type="button" className={styles.collapsibleHeader} onClick={() => setOpen((prev) => !prev)} aria-expanded={open}>
        <Text className={styles.sectionTitle}>
          {title}
          {count != null && <span className={styles.sectionCount}> ({count})</span>}
        </Text>
        <DownOutlined className={`${styles.collapseChevron} ${open ? styles.collapseChevronOpen : ""}`} />
      </button>
      {open && <div className={styles.collapsibleBody}>{children}</div>}
    </div>
  )
}

CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node
}

const BookingDetailSummary = ({
  bookingId,
  guestCount = 0,
  maxGuests = 0,
  reservationCount = 0,
  experienceType = "",
  received = 0,
  outstanding = 0,
  totalBooked = 0,
  potentialRevenue = 0,
  initialCrew = [],
  initialEquipment = [],
  notes = []
}) => {
  const occupancy = maxGuests > 0 ? Math.round((guestCount / maxGuests) * 100) : 0
  const experienceTypeLabel = toTitleCase(experienceType)

  return (
    <>
      {/* Capacity Summary */}
      <div className={styles.sectionCard}>
        <Flex align="center" justify="space-between" className={styles.sectionCardHeader}>
          <Text className={styles.sectionTitle}>
            Capacity Summary <span className={styles.occupancyBadge}>{occupancy}%</span>
          </Text>
          {experienceTypeLabel && (
            <Text className={styles.ticketedLabel}><TicketedIcon width={14} height={11} fill="currentColor" /> {experienceTypeLabel}</Text>
          )}
        </Flex>
        <Flex vertical gap={4}>
          <Flex align="center" gap={6}>
            <Text className={styles.summaryLabel}>Guests</Text>
            <Text className={styles.summaryValue}>
              {guestCount} / {maxGuests}
            </Text>
          </Flex>
          <Flex align="center" gap={6}>
            <Text className={styles.summaryLabel}>Reservations</Text>
            <Text className={styles.summaryValue}>{reservationCount}</Text>
          </Flex>
        </Flex>
      </div>

      {/* Payment Summary */}
      <div className={styles.sectionCard}>
        <Text className={styles.sectionTitle}>Payment Summary</Text>
        <Flex vertical gap={4}>
          <Flex justify="space-between" align="center">
            <Text className={styles.summaryLabel}>Received</Text>
            <Text className={`${styles.summaryValue} ${styles.summaryValueSuccess}`}>{numberToCurrency(received)}</Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text className={styles.summaryLabel}>Outstanding</Text>
            <Text className={`${styles.summaryValue} ${styles.summaryValueWarning}`}>{numberToCurrency(outstanding)}</Text>
          </Flex>
          <div className={styles.divider} />
          <Flex justify="space-between" align="center">
            <Text className={`${styles.summaryLabel} ${styles.summaryBold}`}>Total Booked</Text>
            <Text className={`${styles.summaryValue} ${styles.summaryBold}`}>{numberToCurrency(totalBooked)}</Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text className={styles.summaryLabel}>Potential Additional Revenue</Text>
            <Text className={styles.summaryValue}>{numberToCurrency(potentialRevenue)}</Text>
          </Flex>
        </Flex>
      </div>

      {/* Resources */}
      <CollapsibleSection title="Resources" count={initialCrew.length + initialEquipment.length}>
        <Flex vertical gap={12}>
          <Flex vertical gap={4}>
            <span className={styles.resourceGroupLabel}>Crew</span>
            <CrewCell initialCrew={initialCrew} bookingId={bookingId} emptyLabel="No crew assigned" alwaysEditable />
          </Flex>
          <Flex vertical gap={4}>
            <span className={styles.resourceGroupLabel}>Equipment</span>
            <ResourceCell initialResources={initialEquipment} bookingId={bookingId} emptyLabel="No equipment assigned" alwaysEditable />
          </Flex>
        </Flex>
      </CollapsibleSection>

      {/* Notes */}
      <CollapsibleSection title="Notes" count={notes.length}>
        <NotesSection bookingId={bookingId} notes={notes} hideTitle />
      </CollapsibleSection>
    </>
  )
}

BookingDetailSummary.propTypes = {
  guestCount: PropTypes.number,
  maxGuests: PropTypes.number,
  reservationCount: PropTypes.number,
  experienceType: PropTypes.string,
  received: PropTypes.number,
  outstanding: PropTypes.number,
  totalBooked: PropTypes.number,
  potentialRevenue: PropTypes.number,
  bookingId: PropTypes.string,
  initialCrew: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, role: PropTypes.string })),
  initialEquipment: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  notes: PropTypes.array
}

export default BookingDetailSummary
